/**
 * Meld validation: what counts as a set and what counts as a run.
 * See scope.md §3.2. Every rule that families argue about is a `RoomRules` option.
 */

import {
  type Card, type JokerStandsFor, type Meld, type MeldKind, type NaturalRank,
  type RoomRules, type Suit,
  MIN_RUN, aceInterpretations, isJoker, rankFromRunOrder, runOrder,
} from '@garbage/shared';

export type MeldProblem = string;
export type Validity = { ok: true } | { ok: false; reason: MeldProblem };

const ok: Validity = { ok: true };
const bad = (reason: MeldProblem): Validity => ({ ok: false, reason });

/** What each card in the meld effectively is, once jokers are resolved. */
export type Effective = { rank: NaturalRank; suit?: Suit; joker: boolean };

export function resolve(cards: readonly Card[], jokers: readonly JokerStandsFor[]): Effective[] | null {
  const byId = new Map(jokers.map((j) => [j.cardId, j]));
  const out: Effective[] = [];
  for (const card of cards) {
    if (isJoker(card)) {
      const stands = byId.get(card.id);
      // A joker with nothing declared cannot be checked, so it cannot be allowed.
      if (!stands) return null;
      out.push({ rank: stands.rank, ...(stands.suit ? { suit: stands.suit } : {}), joker: true });
    } else {
      out.push({ rank: card.rank as NaturalRank, ...(card.suit ? { suit: card.suit } : {}), joker: false });
    }
  }
  return out;
}

function commonChecks(cards: readonly Card[], _rules: RoomRules, minimum: number): Validity {
  if (cards.length < minimum) return bad(`A ${minimum === 3 ? 'set' : 'run'} needs at least ${minimum} cards`);
  // A joker is simply wild — there is no cap on how many a meld may hold. The one
  // limit is that an all-joker meld stands for nothing anybody could check or swap
  // against, so a meld needs at least one real card.
  if (cards.every(isJoker)) return bad('A meld needs at least one real card');
  if (new Set(cards.map((c) => c.id)).size !== cards.length) return bad('The same card twice');
  return ok;
}

/** A set: three or more of the same rank. Suits may repeat — two packs make that unavoidable. */
export function validateSet(
  cards: readonly Card[], jokers: readonly JokerStandsFor[], rules: RoomRules,
): Validity {
  const basic = commonChecks(cards, rules, 3);
  if (!basic.ok) return basic;

  const effective = resolve(cards, jokers);
  if (!effective) return bad('Say what the joker stands for');

  const rank = effective.find((e) => !e.joker)?.rank;
  if (!rank) return bad('A meld needs at least one real card');
  if (effective.some((e) => e.rank !== rank)) return bad('A set must be all the same rank');
  return ok;
}

/** A run: four or more of the same suit in consecutive order. */
/**
 * A run is four or more of the same suit in consecutive order. Four is a hard floor,
 * not a setting — a contract may ask for longer, never shorter.
 */
export function validateRun(
  cards: readonly Card[], jokers: readonly JokerStandsFor[], rules: RoomRules,
  minimum = MIN_RUN,
): Validity {
  if (minimum < MIN_RUN) throw new Error(`A run is always at least ${MIN_RUN} cards`);
  const basic = commonChecks(cards, rules, minimum);
  if (!basic.ok) return basic;

  const effective = resolve(cards, jokers);
  if (!effective) return bad('Say which card the joker stands for');
  if (effective.some((e) => !e.suit)) return bad('A joker in a run needs a suit as well as a rank');

  const suit = effective[0]!.suit;
  if (effective.some((e) => e.suit !== suit)) return bad('A run must be all one suit');

  // Ace-low and ace-high are separate readings; either may satisfy the rule.
  for (const aceHigh of aceInterpretations(rules.ace)) {
    const orders = effective.map((e) => runOrder(e.rank, aceHigh));
    let consecutive = true;
    for (let i = 1; i < orders.length; i += 1) {
      if (orders[i]! !== orders[i - 1]! + 1) { consecutive = false; break; }
    }
    if (consecutive) return ok;
  }
  return bad('A run must go up one at a time, in order');
}

export function validateMeld(
  kind: MeldKind, cards: readonly Card[], jokers: readonly JokerStandsFor[], rules: RoomRules,
  minimum?: number,
): Validity {
  return kind === 'set'
    ? validateSet(cards, jokers, rules)
    : validateRun(cards, jokers, rules, Math.max(MIN_RUN, minimum ?? MIN_RUN));
}

/** Re-check a whole meld as it now stands. Used after lay-offs and joker swaps. */
export function meldIsValid(meld: Meld, rules: RoomRules): Validity {
  // Always the real floor. Using the meld's own length here would let a short run
  // validate against itself and hide the very problem this check exists to catch.
  return validateMeld(meld.kind, meld.cards, meld.jokers, rules);
}

/**
 * Where a card could be added to a run: immediately below the bottom or above the top.
 * Returns the new card order, or null when it does not fit.
 */
export function extendRun(
  meld: Meld, card: Card, rules: RoomRules, jokerStandsFor?: JokerStandsFor,
): { cards: Card[]; jokers: JokerStandsFor[] } | null {
  const jokers = jokerStandsFor ? [...meld.jokers, jokerStandsFor] : meld.jokers;
  for (const position of [[card, ...meld.cards], [...meld.cards, card]]) {
    const check = validateRun(position, jokers, rules);
    if (check.ok) return { cards: position, jokers };
  }
  return null;
}

/** Add a card to a set. Order does not matter. */
export function extendSet(
  meld: Meld, card: Card, rules: RoomRules, jokerStandsFor?: JokerStandsFor,
): { cards: Card[]; jokers: JokerStandsFor[] } | null {
  const jokers = jokerStandsFor ? [...meld.jokers, jokerStandsFor] : meld.jokers;
  const cards = [...meld.cards, card];
  return validateSet(cards, jokers, rules).ok ? { cards, jokers } : null;
}

export function extendMeld(
  meld: Meld, card: Card, rules: RoomRules, jokerStandsFor?: JokerStandsFor,
): { cards: Card[]; jokers: JokerStandsFor[] } | null {
  return meld.kind === 'run'
    ? extendRun(meld, card, rules, jokerStandsFor)
    : extendSet(meld, card, rules, jokerStandsFor);
}

/**
 * The ways a joker could be laid off on this meld — what it would have to stand for.
 *
 * A set takes the set's own rank. A run takes the card one below the bottom or one
 * above the top, in the run's suit. Without this a player who takes a joker off the
 * table cannot put it anywhere, and since they also cannot discard while holding it
 * (§3.6), their turn deadlocks.
 */
export function jokerOptionsFor(
  meld: Meld, jokerCard: Card, rules: RoomRules,
): JokerStandsFor[] {
  if (!isJoker(jokerCard)) return [];

  if (meld.kind === 'set') {
    const rank = resolve(meld.cards, meld.jokers)?.find((e) => !e.joker)?.rank;
    if (!rank) return [];
    const option: JokerStandsFor = { cardId: jokerCard.id, rank };
    return extendSet(meld, jokerCard, rules, option) ? [option] : [];
  }

  const effective = resolve(meld.cards, meld.jokers);
  const suit = effective?.find((e) => !e.joker)?.suit;
  if (!effective || !suit) return [];

  const out: JokerStandsFor[] = [];
  for (const aceHigh of aceInterpretations(rules.ace)) {
    const orders = effective.map((e) => runOrder(e.rank, aceHigh));
    for (const order of [Math.min(...orders) - 1, Math.max(...orders) + 1]) {
      const rank = rankFromRunOrder(order, aceHigh);
      if (!rank) continue;
      const option: JokerStandsFor = { cardId: jokerCard.id, rank, suit };
      if (extendRun(meld, jokerCard, rules, option)) out.push(option);
    }
  }
  return out;
}

/** Does this card match what the joker in that meld stands for? scope.md §3.6. */
export function cardMatchesJoker(card: Card, stands: JokerStandsFor, kind: MeldKind): boolean {
  if (isJoker(card)) return false;
  if (card.rank !== stands.rank) return false;
  // A run's joker is one exact card; a set's joker is only a rank.
  if (kind === 'run') return card.suit === stands.suit;
  return true;
}

export { rankFromRunOrder };
